import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import CoachAccreditationExplainerDialog, {
  getAccreditationExplainer,
} from '@components/coach-accreditation-explainer-dialog'
import { buildCoachAccreditations } from '@lib/pga-coach/coach-credentials'
import { useCoachProfileInColumn } from '@lib/pga-coach/coach-profile-layout'
import useStyles from './coach-accreditations-row.styles'

const HEADING = 'PGA Accreditations'
const HEADING_ID = 'coach-accreditations-row-heading'

/**
 * Returns the ordered list of accreditation cards for a coach. Used by
 * the gating helper below and by the component itself, so SSR + tests
 * stay aligned on the same derivation.
 */
function deriveAccreditations(coach) {
  return buildCoachAccreditations({
    memberType: coach?.memberType,
    certificationLevel: coach?.certificationLevel,
    admCertified: coach?.admCertified === true,
    pgaHopeCoach: coach?.coach?.pgaHopeCoach === true,
    hasPgaHopeProgram: coach?.hasPgaHopeProgram === true,
  })
}

/**
 * `true` when the coach has at least one accreditation to surface.
 * Mirrors `coachToolsRowHasContent` so callers can gate adjacent UI
 * (e.g. a two-column Tools | PGA Accreditations row) without re-deriving
 * rules.
 */
export function coachAccreditationsRowHasContent(coach) {
  return deriveAccreditations(coach).length > 0
}

/**
 * Simple PGA Accreditations text list for the flagged About bio. Sits
 * beside Tools & Technology — same bold `Typography` subtitle1 / h3
 * header and body1 bullets, text labels only (no logos, not outlined
 * chips). Each label with an explainer is an underlined button that
 * opens the shared `CoachAccreditationExplainerDialog` (same modal as
 * the membership banner Read More). Hash destination
 * `#coach-accreditations-row-heading` stays on the heading.
 *
 * Auto-hidden when the coach has none of the supported
 * accreditations.
 *
 * Accreditation derivation lives in `@lib/pga-coach/coach-credentials`
 * (`buildCoachAccreditations`) — extend that helper, not this
 * component, when the product surface gains a new badge.
 */
function CoachAccreditationsRow({
  coach = null,
  embedded = false,
  onOpenExplainer,
}) {
  const accreditations = useMemo(() => deriveAccreditations(coach), [
    coach?.memberType,
    coach?.certificationLevel,
    coach?.admCertified,
    coach?.coach?.pgaHopeCoach,
    coach?.hasPgaHopeProgram,
  ])
  const inColumn = useCoachProfileInColumn()
  const compact = embedded || inColumn
  const styles = useStyles({ inColumn: compact, embedded: compact })
  const [openAccreditationId, setOpenAccreditationId] = useState(null)
  const ownsDialog = typeof onOpenExplainer !== 'function'

  const handleOpen = useCallback((id) => {
    if (ownsDialog) {
      setOpenAccreditationId(id)
      return
    }
    onOpenExplainer(id)
  }, [ownsDialog, onOpenExplainer])

  const handleClose = useCallback(() => {
    setOpenAccreditationId(null)
  }, [])

  if (accreditations.length === 0) return null

  const rootClassName = compact ? styles.rootEmbedded : styles.root
  const innerClassName = compact
    ? `${styles.inner} ${styles.innerEmbedded}`
    : styles.inner

  return (
    <>
      <Box
        component="section"
        className={rootClassName}
        aria-labelledby={HEADING_ID}
      >
        <Box className={innerClassName}>
          <Typography
            id={HEADING_ID}
            variant="subtitle1"
            component="h3"
            className={styles.heading}
          >
            {HEADING}
          </Typography>
          <Box component="ul" className={styles.list}>
            {accreditations.map(({ id, label }) => {
              const explainer = getAccreditationExplainer(id)
              return (
                <Typography
                  key={id}
                  component="li"
                  variant="body1"
                  className={styles.item}
                >
                  {explainer ? (
                    <button
                      type="button"
                      className={styles.itemButton}
                      aria-haspopup="dialog"
                      onClick={() => handleOpen(id)}
                    >
                      {label}
                    </button>
                  ) : (
                    label
                  )}
                </Typography>
              )
            })}
          </Box>
        </Box>
      </Box>
      {ownsDialog ? (
        <CoachAccreditationExplainerDialog
          accreditationId={openAccreditationId}
          onClose={handleClose}
        />
      ) : null}
    </>
  )
}

CoachAccreditationsRow.propTypes = {
  coach: PropTypes.shape({
    memberType: PropTypes.string,
    certificationLevel: PropTypes.string,
    admCertified: PropTypes.bool,
    coach: PropTypes.shape({
      pgaHopeCoach: PropTypes.bool,
    }),
    hasPgaHopeProgram: PropTypes.bool,
  }),
  /** When true, renders inside the About bio (no full-bleed band chrome). */
  embedded: PropTypes.bool,
  /**
   * Optional. Called with the accreditation `id` instead of mounting
   * this row's own dialog — same callback as `MembershipTag`
   * `onOpenExplainer`. Flagged `CoachBio` passes this so the
   * membership banner and these bullets share one
   * `CoachAccreditationExplainerDialog`. Omit in Storybook so the
   * row opens the dialog itself.
   */
  onOpenExplainer: PropTypes.func,
}

CoachAccreditationsRow.defaultProps = {
  onOpenExplainer: undefined,
}

export default CoachAccreditationsRow
